/**
 * Slice 5b — shared broadcast body assembler.
 *
 * The owner composer in apps/web and the wallet send worker in apps/api both
 * need to produce the exact same string that Google receives. Live preview
 * (composer) and live send (worker) must never disagree, so this lives in
 * @naya/shared and both ends import it.
 *
 * Validity columns can be in one of three shapes (see BroadcastValiditySchema
 * in schemas/index.ts). When none are set, the assembled body is identical
 * to the raw body — slice 5 broadcasts continue to render unchanged.
 *
 * Timezone: dates always render in Asia/Manila. No timezone label is shown
 * since every merchant in V1 is in PH; revisit if/when multi-region.
 */
const PH_TIMEZONE = 'Asia/Manila';
/**
 * Combine the raw body with the optional validity line. Returns the body
 * unchanged when no validity is set.
 */
export function assembleBroadcastBody(input) {
    const line = formatValidityLine(input.validity);
    return line ? `${input.body}\n\n${line}` : input.body;
}
/**
 * Render the validity line itself. Returns null when no validity is set or
 * the inputs are inconsistent (shouldn't happen — service-layer validation
 * catches this — but defensive null beats throwing during a live send).
 */
export function formatValidityLine(v) {
    if (!v.validity_kind)
        return null;
    if (v.validity_kind === 'text') {
        if (!v.validity_text)
            return null;
        return `Valid: ${v.validity_text}`;
    }
    if (v.validity_kind === 'date') {
        const end = toDate(v.validity_end_at);
        if (!end)
            return null;
        return `Valid until ${formatPHDate(end, v.validity_includes_time)}`;
    }
    if (v.validity_kind === 'range') {
        const start = toDate(v.validity_start_at);
        const end = toDate(v.validity_end_at);
        if (!start || !end)
            return null;
        return `Valid ${formatPHDate(start, v.validity_includes_time)} – ${formatPHDate(end, v.validity_includes_time)}`;
    }
    return null;
}
function toDate(v) {
    if (!v)
        return null;
    if (v instanceof Date)
        return v;
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
}
/**
 * Format a Date in Asia/Manila. With time: "May 20, 2026, 3:00 PM". Without:
 * "May 20, 2026". Uses Intl.DateTimeFormat so both Node (worker) and browser
 * (composer preview) produce the same output.
 */
function formatPHDate(d, includeTime) {
    const dateFmt = new Intl.DateTimeFormat('en-US', {
        timeZone: PH_TIMEZONE,
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
    if (!includeTime)
        return dateFmt.format(d);
    const timeFmt = new Intl.DateTimeFormat('en-US', {
        timeZone: PH_TIMEZONE,
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    });
    return `${dateFmt.format(d)}, ${timeFmt.format(d)}`;
}
//# sourceMappingURL=format.js.map